import { z } from 'zod';
import { paymentSchema } from './schemas-contracts';

/** Faktury wystawiane z CRM przez system fakturowy (Fakturownia). */

export const INVOICE_KINDS = {
  zamowienie: 'Zamówienie (podstawa zaliczek)',
  proforma: 'Faktura pro forma',
  zaliczkowa: 'Faktura zaliczkowa',
  koncowa: 'Faktura końcowa',
  vat: 'Faktura VAT',
  korekta: 'Faktura korygująca',
} as const;
export type InvoiceKind = keyof typeof INVOICE_KINDS;

export const INVOICE_STATUSES = {
  wysylanie: 'Wysyłanie',
  wystawiona: 'Wystawiona',
  blad: 'Błąd',
  niepewna: 'Do sprawdzenia',
  anulowana: 'Porzucona',
} as const;
export type InvoiceStatus = keyof typeof INVOICE_STATUSES;

export const INTEGRATION_MODES = { wylaczona: 'Wyłączona', test: 'Testowa', produkcja: 'Produkcyjna' } as const;

/** Kto wysyła dokumenty (pro forma, faktury) do klienta. */
export const EMAIL_MODES = {
  crm: 'Z CRM (poczta CRM, PDF w załączniku)',
  fakturownia: 'Z Fakturowni (jej wysyłka e-mail)',
  brak: 'Nie wysyłaj — tylko przycisk „Wyślij”',
} as const;
export type EmailMode = keyof typeof EMAIL_MODES;
export type IntegrationMode = keyof typeof INTEGRATION_MODES;

/** Stan KSeF z Fakturowni (pole gov_status) → opis dla użytkownika. */
export const KSEF_STATUS_LABELS: Record<string, string> = {
  processing: 'KSeF: przetwarzanie',
  ok: 'KSeF: przyjęta',
  send_error: 'KSeF: błąd wysyłki',
  server_error: 'KSeF: błąd serwera',
  status_check_error: 'KSeF: błąd sprawdzenia',
  offline: 'KSeF: tryb offline',
  offline_error: 'KSeF: błąd offline',
  duplicate_error: 'KSeF: duplikat',
  blocked_403_error: 'KSeF: brak uprawnień',
  not_applicable: 'KSeF: nie dotyczy',
  not_connected: 'KSeF: nie połączono',
  demo_ok: 'KSeF (demo): przyjęta',
  demo_processing: 'KSeF (demo): przetwarzanie',
};

export const EXT_STATUS_LABELS: Record<string, string> = {
  issued: 'nieopłacona',
  sent: 'wysłana, nieopłacona',
  paid: 'opłacona',
  partial: 'częściowo opłacona',
  rejected: 'odrzucona',
};

export const connectionUpdateSchema = z.object({
  mode: z.enum(['wylaczona', 'test', 'produkcja']),
  accountPrefix: z
    .string()
    .trim()
    .toLowerCase()
    .regex(/^[a-z0-9][a-z0-9-]{0,62}$/, 'Prefiks konta: litery, cyfry i myślniki (np. ekocrm z adresu ekocrm.fakturownia.pl)'),
  departmentId: z.number().int().positive().nullable(),
  /** nowy token — tylko na wejściu; pusty = bez zmian */
  token: z.string().trim().max(200).optional(),
  emailMode: z.enum(['crm', 'fakturownia', 'brak']).default('crm'),
  /** wysyłaj klientowi automatycznie po wystawieniu (pro forma, faktury) */
  autoSend: z.boolean().default(true),
});
export type ConnectionUpdateInput = z.infer<typeof connectionUpdateSchema>;

export interface ConnectionDto {
  companyId: string;
  provider: 'fakturownia';
  mode: IntegrationMode;
  accountPrefix: string;
  departmentId: number | null;
  /** ostatnie znaki tokenu, np. „••••a1b2”; pusty = brak tokenu */
  tokenHint: string;
  tokenSetAt: string | null;
  status: 'nieskonfigurowana' | 'ok' | 'blad';
  lastCheckAt: string | null;
  lastError: string;
  /** czy serwer ma klucz do szyfrowania tokenów */
  keyConfigured: boolean;
  emailMode: EmailMode;
  autoSend: boolean;
}

export interface FakturowniaDepartmentDto {
  id: number;
  name: string;
  taxNo: string;
}

export interface ConnectionCheckDto {
  ok: boolean;
  message: string;
  departments: FakturowniaDepartmentDto[];
}

export interface InvoiceItemDto {
  position: number;
  name: string;
  unit: string;
  quantity: number;
  netGr: number;
  grossGr: number;
  vatRate: string;
}

export interface InvoiceDto {
  id: string;
  contractId: string;
  kind: InvoiceKind;
  status: InvoiceStatus;
  mode: IntegrationMode;
  number: string | null;
  netGr: number | null;
  vatGr: number | null;
  grossGr: number | null;
  issueDate: string | null;
  sellDate: string | null;
  paymentTo: string | null;
  extStatus: string | null;
  paidGr: number | null;
  ksefStatus: string | null;
  ksefNumber: string | null;
  paymentId: string | null;
  paymentItemId: string | null;
  emailStatus: 'wyslano' | 'blad' | 'brak_adresu' | null;
  emailTo: string | null;
  emailSentAt: string | null;
  parentNumber: string | null;
  settledByNumber: string | null;
  /** pro forma: faktury zaliczkowe wystawione z wpłat do tej samej pozycji harmonogramu */
  convertedTo: string[];
  /** pro forma w całości zamieniona na faktury zaliczkowe (już nie jest wezwaniem do zapłaty) */
  converted: boolean;
  /** faktura zaliczkowa: pro forma tej pozycji harmonogramu, z której powstała */
  fromProformaNumber: string | null;
  correctionReason: string;
  lastError: string;
  requestedAt: string;
  requestedByName: string | null;
  items: InvoiceItemDto[];
  /** wpłaty zarejestrowane w CRM do tej faktury, jeszcze niewysłane do Fakturowni (do ponowienia) */
  pendingPayments: PendingInvoicePaymentDto[];
  /** pozostało do zapłaty — saldo faktury (invoiceBalanceGr: brutto − rozliczone zaliczki − wpłaty); null — nie dotyczy */
  toPayGr: number | null;
  /** faktura końcowa: suma faktur zaliczkowych rozliczonych tą fakturą */
  advancesGr?: number;
  /** faktura VAT do aneksu: numer aneksu (P1) */
  annexNumber?: string | null;
  can: { pdf: boolean; refresh: boolean; retry: boolean; abandon: boolean; correct: boolean; send: boolean; registerPayment: boolean; syncPayments: boolean };
}

/** Faktura do wpłaty: zaliczkowa, a dla wpłaty zamykającej płatność — końcowa (lub VAT, gdy jedna wpłata za całość). */
export interface PaymentInvoicingDto {
  paymentId: string;
  amountGr: number;
  paidAt: string;
  itemName: string;
  invoice: { id: string; number: string | null; kind: InvoiceKind; status: InvoiceStatus } | null;
  /** zaliczkowa — do wystawienia; null — już jest albo wpłata do płatności końcowej (rozlicza ją faktura końcowa) */
  next: 'zaliczkowa' | null;
  /** wpłata do pozycji „płatność końcowa” — rozlicza ją faktura końcowa, nie zaliczkowa */
  toFinal: boolean;
  /** wpłata na płatność podpisanego aneksu — rozlicza ją faktura VAT do aneksu (P1); numer aneksu */
  annexNumber?: string | null;
  /** dlaczego nie można wystawić teraz */
  blockReason: string;
  /** termin wystawienia: 15. dzień miesiąca po otrzymaniu wpłaty */
  deadline: string;
  overdue: boolean;
}

/** Termin wystawienia faktury do otrzymanej wpłaty — 15. dzień następnego miesiąca. */
export function invoiceDeadline(paidAt: string): string {
  const [y, m] = paidAt.split('-').map(Number) as [number, number];
  const ny = m === 12 ? y + 1 : y;
  const nm = m === 12 ? 1 : m + 1;
  return `${ny}-${String(nm).padStart(2, '0')}-15`;
}

/**
 * P2: saldo faktury końcowej / VAT — jedna reguła dla kwoty „do zapłaty”, przycisku i limitu wpłaty, rejestru sprzedaży,
 * przeglądu Finansów, e-maila i warunku zamknięcia sprawy: brutto − faktury zaliczkowe rozliczone tą fakturą − wpłaty
 * (opłacone w Fakturowni albo zarejestrowane w CRM, większa z kwot). Saldo 0 = faktura opłacona (niezależnie od statusu
 * w Fakturowni). null — brak kwoty brutto (faktura jeszcze nie wystawiona).
 */
export function invoiceBalanceGr(i: { grossGr: number | null; paidGr?: number | null; paidSyncedGr?: number | null }): number | null {
  if (i.grossGr === null) return null;
  return Math.max(i.grossGr - Math.max(i.paidGr ?? 0, i.paidSyncedGr ?? 0), 0);
}

/** Rozkład kwoty na pozycje od najstarszej (kolejność listy), nie więcej niż pozostało na pozycji (P3). */
export function allocateOldestFirst<T extends { leftGr: number }>(amountGr: number, items: T[]): { item: T; amountGr: number }[] {
  const out: { item: T; amountGr: number }[] = [];
  let left = amountGr;
  for (const it of items) {
    if (left <= 0) break;
    const part = Math.min(left, Math.max(it.leftGr, 0));
    if (part > 0) out.push({ item: it, amountGr: part });
    left -= part;
  }
  return out;
}

/** Pozycja harmonogramu z dokumentami: pro forma (transze) albo faktura końcowa (ostatnia pozycja). */
export interface ScheduleInvoicingDto {
  paymentItemId: string;
  name: string;
  amountGr: number;
  paidGr: number;
  /** ostatnia pozycja harmonogramu — płatność końcowa (faktura końcowa z terminem) */
  isFinal: boolean;
  proforma: { id: string; number: string | null; status: InvoiceStatus; emailStatus: InvoiceDto['emailStatus'] } | null;
  canProforma: boolean;
  proformaBlock: string;
  /** P1: pozycja podpisanego aneksu (fakturuje ją faktura VAT do aneksu) — id i numer aneksu */
  annexContractId?: string | null;
  annexNumber?: string | null;
  /** P3: faktura końcowa / VAT, która obejmuje tę pozycję (wpłaty — przy tej fakturze) */
  coveredBy?: { id: string; number: string | null } | null;
  /** QA P4: pro forma do pozycji po terminie przedłuży termin — ostrzeżenie przed wystawieniem */
  proformaWarning?: string | null;
}

/** P1: faktura VAT do podpisanego aneksu (pozycje aneksu, rozlicza płatności aneksu). */
export interface AnnexInvoicingDto {
  contractId: string;
  number: string | null;
  /** wartość brutto pozycji aneksu */
  grossGr: number;
  /** płatności aneksu w harmonogramie i wpłaty na nie */
  scheduleGr: number;
  alreadyPaidGr: number;
  invoice: { id: string; number: string | null; status: InvoiceStatus } | null;
  allowed: boolean;
  reason: string;
  /** ostrzeżenia przed wystawieniem (bez blokady): prace aneksu nieodebrane */
  warnings: string[];
  /** QA6 P18: podpowiedź terminu z harmonogramu aneksu (jak `finalDue` umowy); brak — gdy wystawić nie można */
  due?: { days: number; scheduleDue: string | null; schedulePassed: boolean };
}

/** Faktury umowy i co można teraz wystawić. */
export interface ContractInvoicingDto {
  /** stan połączenia firmy umowy; null — firma bez integracji */
  connection: { mode: IntegrationMode; status: ConnectionDto['status'] } | null;
  invoices: InvoiceDto[];
  /** wpłaty umowy z fakturą zaliczkową albo informacją, że trzeba ją wystawić */
  payments: PaymentInvoicingDto[];
  schedule: ScheduleInvoicingDto[];
  /** QA P4: domyślny termin faktury końcowej / VAT z harmonogramu (najpóźniejszy termin rozliczanych pozycji) */
  finalDue?: { days: number; scheduleDue: string | null; schedulePassed: boolean };
  /** wysyłka dokumentów do klienta */
  email: { mode: EmailMode; autoSend: boolean; to: string | null };
  final: {
    allowed: boolean;
    kind: 'koncowa' | 'vat';
    reason: string;
    advancesGr: number;
    contractGrossGr: number;
    paymentItemId: string | null;
    alreadyPaidGr: number;
    /** ostrzeżenia przed wystawieniem (bez blokady — np. faktura VAT na życzenie klienta): brak odbioru, umowa niepodpisana */
    warnings?: string[];
  };
  /** P1: podpisane aneksy z pozycjami — osobna faktura VAT do każdego */
  annexes?: AnnexInvoicingDto[];
  can: { issue: boolean; correct: boolean };
  /** ostrzeżenia przy wystawianiu (nie blokują) — np. firma bez numeru rachunku (D9) */
  warnings: string[];
}

/** Umowa, do której należy pozycja przeglądu faktur (Finanse). */
interface InvoicingOverviewRef {
  contractId: string;
  contractNumber: string | null;
  caseId: string;
  caseNumber: string;
  clientName: string;
  companyId: string;
}

/** Wpłata bez faktury zaliczkowej — do wystawienia najpóźniej 15. dnia następnego miesiąca. */
export interface UninvoicedPaymentDto extends InvoicingOverviewRef {
  paymentId: string;
  itemName: string;
  amountGr: number;
  paidAt: string;
  deadline: string;
  overdue: boolean;
  /** dlaczego nie można wystawić teraz (pusty — można) */
  blockReason: string;
}

/** Wystawiona faktura końcowa / VAT, której Fakturownia nie oznaczyła jako opłaconej w całości. */
export interface UnpaidInvoiceDto extends InvoicingOverviewRef {
  invoiceId: string;
  kind: 'koncowa' | 'vat';
  number: string | null;
  mode: IntegrationMode;
  grossGr: number | null;
  paidGr: number;
  issueDate: string | null;
  paymentTo: string | null;
  extStatus: string | null;
  overdue: boolean;
  toPayGr: number | null;
  pendingPayments: PendingInvoicePaymentDto[];
  /** może zarejestrować wpłatę do faktury (finanse.wplaty + faktury.wystawianie W/F) */
  canRegisterPayment: boolean;
}

/** Pozycja harmonogramu wymagająca pro formy: zdarzenie pozycji zaszło, a pro formy jeszcze nie ma. */
export interface ProformaDueDto extends InvoicingOverviewRef {
  paymentItemId: string;
  itemName: string;
  amountGr: number;
  paidGr: number;
  dueDate: string | null;
  /** dlaczego nie można wystawić teraz (pusty — można) */
  blockReason: string;
}

/** Umowa do faktury końcowej / VAT: płatność końcowa jest należna albo już wpłacona, a faktury jeszcze nie ma. */
export interface FinalDueDto extends InvoicingOverviewRef {
  kind: 'koncowa' | 'vat';
  /** pozycja harmonogramu „płatność końcowa” (null — umowa bez harmonogramu) */
  itemName: string | null;
  contractGrossGr: number;
  advancesGr: number;
  alreadyPaidGr: number;
  /** dlaczego nie można wystawić teraz (pusty — można) */
  blockReason: string;
  /** P1: faktura VAT do podpisanego aneksu (contractId — umowa główna) */
  annexContractId?: string | null;
  annexNumber?: string | null;
}

/** Przegląd fakturowania na stronie Finanse — tylko umowy, do których finansów użytkownik ma dostęp. */
export interface InvoicingOverviewDto {
  /** stan integracji firm widocznych dla użytkownika */
  companies: { companyId: string; connected: boolean; mode: IntegrationMode | null }[];
  uninvoicedPayments: UninvoicedPaymentDto[];
  /** pro formy do wystawienia (pozycje z wymaganą pro formą, których zdarzenie zaszło) */
  proformaDue: ProformaDueDto[];
  /** faktury końcowe / VAT do wystawienia */
  finalDue: FinalDueDto[];
  unpaidInvoices: UnpaidInvoiceDto[];
  /** czy użytkownik może wystawiać faktury (w którejkolwiek firmie) — przyciski w przeglądzie */
  canIssue: boolean;
}

/** Wpłata zarejestrowana w CRM do faktury, której Fakturownia jeszcze nie przyjęła. */
export interface PendingInvoicePaymentDto {
  paymentId: string;
  amountGr: number;
  paidAt: string;
}

/** Wpłata do faktury końcowej / VAT z CRM — trafia też do Fakturowni (pozycję harmonogramu bierzemy z faktury). */
export const invoicePaymentSchema = paymentSchema.omit({ paymentItemId: true });
export type InvoicePaymentInput = z.infer<typeof invoicePaymentSchema>;

/** Wynik rejestracji / ponowienia wpłaty: synced=false — zapisana w CRM, Fakturownia do ponowienia (error — powód). */
export interface InvoicePaymentResultDto {
  invoicing: ContractInvoicingDto;
  synced: boolean;
  error: string;
}

export const invoiceCorrectionSchema = z.object({
  reason: z.string().trim().min(3, 'Podaj przyczynę korekty').max(256),
  /** zero — korekta do zera (anulowanie faktury w KSeF); zmiana — nowe ilości / kwoty pozycji */
  mode: z.enum(['zero', 'zmiana']),
  items: z
    .array(z.object({ position: z.number().int().min(1), quantity: z.number().min(0).max(1_000_000), grossGr: z.number().int().min(0) }))
    .max(200)
    .default([]),
});
export type InvoiceCorrectionInput = z.infer<typeof invoiceCorrectionSchema>;

export const invoiceFinalSchema = z.object({
  /** termin płatności w dniach od wystawienia */
  paymentDays: z.number().int().min(0).max(120).default(14),
  /** QA P17: bez automatycznego e-maila z fakturą (np. do zamknięcia uwag z odbioru) — wysyłka ręczna „Wyślij do klienta” */
  holdEmail: z.boolean().optional(),
});

// ── rejestr faktur sprzedaży (Finanse → Sprzedaż) ─────────────────────────────

/** Rozliczenie faktury w rejestrze. */
export const SALES_PAY_FILTERS = { wszystkie: 'Wszystkie', nieoplacone: 'Nieopłacone', po_terminie: 'Po terminie', oplacone: 'Opłacone' } as const;
export type SalesPayFilter = keyof typeof SALES_PAY_FILTERS;

/** Rodzaje w rejestrze: dokumenty księgowe (bez pro form i zamówień) albo wszystkie. */
export const SALES_DOC_FILTERS = { ksiegowe: 'Faktury (VAT, zaliczkowe, końcowe, korekty)', proformy: 'Pro formy i zamówienia', wszystkie: 'Wszystkie dokumenty' } as const;
export type SalesDocFilter = keyof typeof SALES_DOC_FILTERS;

export const salesRegisterQuerySchema = z.object({
  q: z.string().trim().max(100).optional(),
  companyId: z.string().uuid().optional(),
  docs: z.enum(Object.keys(SALES_DOC_FILTERS) as [SalesDocFilter, ...SalesDocFilter[]]).default('ksiegowe'),
  pay: z.enum(Object.keys(SALES_PAY_FILTERS) as [SalesPayFilter, ...SalesPayFilter[]]).default('wszystkie'),
  /** zakres dat wystawienia RRRR-MM-DD */
  from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});
export type SalesRegisterQuery = z.infer<typeof salesRegisterQuerySchema>;

export interface SalesInvoiceRowDto {
  id: string;
  companyId: string;
  kind: InvoiceKind;
  status: InvoiceStatus;
  mode: IntegrationMode;
  number: string | null;
  ksefNumber: string | null;
  ksefStatus: string | null;
  issueDate: string | null;
  sellDate: string | null;
  paymentTo: string | null;
  buyerName: string;
  buyerNip: string;
  netGr: number | null;
  vatGr: number | null;
  grossGr: number | null;
  /** opłacono (Fakturownia + wpłaty z CRM); null — nie dotyczy (pro forma, zamówienie) */
  paidGr: number | null;
  toPayGr: number | null;
  overdue: boolean;
  extStatus: string | null;
  emailStatus: 'wyslano' | 'blad' | 'brak_adresu' | null;
  parentNumber: string | null;
  /** pro forma: numery faktur zaliczkowych, na które została zamieniona */
  convertedTo: string | null;
  /** umowa — dokument z umowy (sprawy); serwis — faktura za zgłoszenie serwisowe */
  source: 'umowa' | 'serwis';
  /** null przy fakturze za serwis */
  contractId: string | null;
  contractNumber: string | null;
  caseId: string | null;
  /** przy fakturze za serwis — numer zgłoszenia */
  caseNumber: string;
  ticketId: string | null;
}

/**
 * Szczegóły faktury w „Faktury sprzedaży” — jednakowe dla każdego źródła (umowa, serwis, kolejne): pozycje, PDF, wysyłka.
 * Działanie, którego źródło nie obsługuje (np. wpłata do faktury za serwis opłaconej online), ma `can` = false.
 */
export interface SalesInvoiceDetailDto {
  id: string;
  source: SalesInvoiceRowDto['source'];
  /** pozycje jak na fakturze */
  items: InvoiceItemDto[];
  /** adres, na który pójdzie „Wyślij do klienta” */
  emailTo: string | null;
  can: { pdf: boolean; send: boolean; refresh: boolean; registerPayment: boolean };
}

export interface SalesRegisterDto {
  items: SalesInvoiceRowDto[];
  total: number;
  /** sumy całego wyniku (nie tylko strony) — dokumenty wystawione */
  sums: { netGr: number; vatGr: number; grossGr: number; toPayGr: number; overdueGr: number };
}
